import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Wrench, Loader2, Check, AlertCircle } from 'lucide-react';

const TOOL_LABELS: Record<string, string> = {
  search_events: 'Searching events',
  get_event_details: 'Getting event details',
  search_documents: 'Searching documents',
  get_asset_info: 'Looking up assets',
  get_asset_details: 'Getting asset details',
  get_connected_assets: 'Walking equipment graph',
  count_assets_by_type: 'Counting assets by type',
  get_themes: 'Getting themes',
  get_tasks: 'Getting tasks',
  get_site_overview: 'Getting site overview',
};

interface ToolCallCardProps {
  toolName: string;
  args: Record<string, unknown>;
  status: 'pending' | 'complete' | 'error';
  result?: unknown;
}

const ToolCallCard: React.FC<ToolCallCardProps> = ({
  toolName,
  args,
  status,
  result,
}) => {
  const [expanded, setExpanded] = useState(false);
  const label = TOOL_LABELS[toolName] || toolName.replace(/_/g, ' ');

  // Filter out empty args
  const displayArgs = Object.entries(args).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );

  const resultSummary =
    result && typeof result === 'object' && 'total' in (result as Record<string, unknown>)
      ? `${(result as Record<string, unknown>).total} results`
      : result && typeof result === 'object' && 'error' in (result as Record<string, unknown>)
        ? String((result as Record<string, unknown>).error)
        : null;

  return (
    <div className="my-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-tertiary)] overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-[var(--color-surface-hover)] transition-colors"
      >
        <Wrench className="w-3 h-3 text-[var(--color-text-muted)] shrink-0" />
        <span className="text-[11px] font-medium text-[var(--color-text-secondary)] flex-1 truncate">
          {label}
        </span>
        {status === 'pending' && (
          <Loader2 className="w-3 h-3 animate-spin text-[#17c480] shrink-0" />
        )}
        {status === 'complete' && (
          <Check className="w-3 h-3 text-[#17c480] shrink-0" />
        )}
        {status === 'error' && (
          <AlertCircle className="w-3 h-3 text-red-400 shrink-0" />
        )}
        {expanded ? (
          <ChevronDown className="w-3 h-3 text-[var(--color-text-muted)]" />
        ) : (
          <ChevronRight className="w-3 h-3 text-[var(--color-text-muted)]" />
        )}
      </button>

      {expanded && (
        <div className="px-2.5 pb-2 space-y-1.5 border-t border-[var(--color-border)]">
          {displayArgs.length > 0 && (
            <div className="pt-1.5 flex flex-wrap gap-1">
              {displayArgs.map(([key, val]) => (
                <span
                  key={key}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]"
                >
                  {key}: {String(val)}
                </span>
              ))}
            </div>
          )}
          {result !== undefined && (
            <div className="pt-1 text-[10px] text-[var(--color-text-muted)] max-h-32 overflow-y-auto">
              {resultSummary ? (
                <span>{resultSummary}</span>
              ) : (
                <pre className="whitespace-pre-wrap break-words font-mono">
                  {JSON.stringify(result, null, 2).slice(0, 500)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ToolCallCard;
